import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import fr from 'facet-rest';
import DataContainer from 'Components/UI/DataContainer';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { QueryVal } from 'Helpers/Query';
import moment from 'moment';
import useUserContext from 'Context/UserContext';
import { TemplatesProvider } from 'Context/TemplateContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import OutOfOfficeDialogContent from './DialogContent';

const OutOfOfficeDialogContainer = ({
  classes,
  closeModal,
  ...rest
}) => {
  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const { refreshAgent, agentResource } = useUserContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  // Scheduled Messages states
  const [scheduledMessagesLoading, setScheduledMessagesLoading] = useState(false);
  const [meetingAgendaCollection, setMeetingAgendaCollection] = useState(null);

  // Agent Out of office state
  const [outOfOfficeMessage, setOutOfOfficeMessage] = useState('');
  const [scheduledDates, setScheduledDates] = useState({
    start: null,
    end: null,
  });
  const today = moment().startOf('day');
  const [scheduledTimes, setScheduledTimes] = useState({
    start: today,
    end: today,
  });

  // Out of Office Resource
  const [outOfOfficeResource, setOutOfOfficeResource] = useState(null);

  const { rootNodeProps } = useEventTrackingLogEvent({ nodeName: 'Out of Office Dialog' });
  const location = useLocation();

  const updateOutOfOfficeStatus = async () => {
    const originalResource = outOfOfficeResource.copy();
    try {
      outOfOfficeResource.attr('startTime', scheduledDates.start.toISOString());
      outOfOfficeResource.attr('endTime', scheduledDates.end.toISOString());
      outOfOfficeResource.attr('message', outOfOfficeMessage);
      const [updatedOutOfOfficeResource] = await outOfOfficeResource.submit();
      setOutOfOfficeResource(updatedOutOfOfficeResource);
      displaySuccess('Out of office has been set');
      refreshAgent();
    } catch (err) {
      displayErrorMessage(err, err.errors);
      // Reset the resource so the modal doesn't think ooo has been set (startTime && endTime && message)
      setOutOfOfficeResource(originalResource);
    }
  };

  // Fetch Agent meeting agendas
  const getMeetingAgendas = async () => {
    try {
      setScheduledMessagesLoading(true);
      const unreviewedMeetingAgendasColl = await outOfOfficeResource.relationship(fr.relationships.scheduledUnreviewedMeetingAgendas.name).link('related').get();
      setMeetingAgendaCollection(unreviewedMeetingAgendasColl);
      setScheduledMessagesLoading(false);
    } catch (err) {
      displayErrorMessage('Error fetching meeting agendas', err);
    }
  };

  // ReFetch Agent meeting agendas
  const refreshMeetingAgendas = async () => {
    if (!meetingAgendaCollection) return;
    try {
      setScheduledMessagesLoading(true);
      const coll = await meetingAgendaCollection.refresh();
      setMeetingAgendaCollection(coll);
      setScheduledMessagesLoading(false);
    } catch (err) {
      displayErrorMessage('Error fetching meeting agendas', err);
    }
  };

  const outOfOfficeSelfLink = useMemo(() => (QueryVal('outOfOfficeSelfLink') || null), [location.search]);

  const fetchOutOfOffice = async () => {
    try {
      setLoading(true);
      const outOfOfficeCollection = await fr.http.get(outOfOfficeSelfLink);
      let outOfOfficeRes = outOfOfficeCollection.resources().head();
      if (outOfOfficeCollection.resources().length() === 0) {
        outOfOfficeRes = outOfOfficeCollection.create();
      }
      setOutOfOfficeResource(outOfOfficeRes);
    } catch (err) {
      setLoadingError(true);
      displayErrorMessage('Error fetching out of office information', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset Out of Office state
  const returnFromOutOfOffice = async () => {
    if (outOfOfficeResource.link('self')) {
      try {
        // Remove Current OOO resource / update the agent resource outOfOffice state
        await outOfOfficeResource.delete();

        // Reset out of office message state
        setOutOfOfficeMessage('');
        setMeetingAgendaCollection(null);
        setScheduledTimes({
          start: today,
          end: today,
        });

        // Display success / refetch OOO + agent
        displaySuccess('Out of office has been disabled');
        fetchOutOfOffice();
        refreshAgent();
      } catch (err) {
        displayErrorMessage('Error disabling out of office', err);
      }
    }
  };

  // Determines if the agent is out of office
  const outOfOfficeIsEnabled = outOfOfficeResource?.attr('startTime') !== undefined
    && outOfOfficeResource?.attr('endTime') !== undefined
    && outOfOfficeResource?.attr('message') !== undefined;

  useEffect(() => {
    fetchOutOfOffice();
  }, [outOfOfficeSelfLink]);

  useEffect(() => {
    if (!outOfOfficeResource || !outOfOfficeResource.relationship(fr.relationships.scheduledUnreviewedMeetingAgendas.name)) return;
    setOutOfOfficeMessage(outOfOfficeResource.attr('message'));
    setScheduledDates({
      start: moment(outOfOfficeResource.attr('startTime')),
      end: moment(outOfOfficeResource.attr('endTime')),
    });
    setScheduledTimes({
      start: moment(outOfOfficeResource.attr('startTime')),
      end: moment(outOfOfficeResource.attr('endTime')),
    });
    getMeetingAgendas();
  }, [outOfOfficeResource]);

  return (
    <DataContainer
      loading={loading}
      loadingError={loadingError}
      {...rootNodeProps}
    >
      <TemplatesProvider
        templatesLinkUrl={agentResource?.relationship(fr.relationships.outOfOfficeTemplates.name).link('related').url()}
        pinnedTemplatesLinkUrl={agentResource?.relationship(fr.relationships.pinnedOutOfOfficeTemplates.name).link('related').url()}
      >
        <OutOfOfficeDialogContent
          outOfOfficeEnabled={outOfOfficeIsEnabled}
          outOfOfficeMessage={outOfOfficeMessage}
          setOutOfOfficeMessage={setOutOfOfficeMessage}
          updateOutOfOfficeStatus={updateOutOfOfficeStatus}
          scheduledDates={scheduledDates}
          scheduledTimes={scheduledTimes}
          onClearSchedule={returnFromOutOfOffice}
          setScheduledDates={setScheduledDates}
          setScheduledTimes={setScheduledTimes}
          meetingAgendaCollection={meetingAgendaCollection}
          refreshMeetingAgendas={refreshMeetingAgendas}
          scheduledMessagesLoading={scheduledMessagesLoading}
          {...rest}
        />
      </TemplatesProvider>
    </DataContainer>
  );
};


OutOfOfficeDialogContainer.defaultProps = {};
export default OutOfOfficeDialogContainer;
